import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../U3130';
import type { U3130HonorariumItem, U3130Timesheet } from '../../U3130TimesheetModel';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { U2000TimesheetWeekDay } from '../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import { u3130GroupHonorariumForDisplay } from '../../tools/u3130GroupHonorariumForDisplay';
import {
  u3130SortHonorariumProjectGroups,
  type U3130HonorariumProjectSortField,
  type U3130HonorariumSortOrder,
} from '../../tools/u3130HonorariumGridSort';
import {
  U3130_HONORARIUM_CELL_X,
  U3130_HONORARIUM_COL_WIDTH_CHECKBOX,
  U3130_HONORARIUM_COL_WIDTH_CLIENT_MIN,
  U3130_HONORARIUM_COL_WIDTH_HOUR,
  U3130_HONORARIUM_COL_WIDTH_NATURES,
  U3130_HONORARIUM_COL_WIDTH_PROJECT,
  U3130_HONORARIUM_COL_WIDTH_SUBPROJECT,
  U3130_HONORARIUM_DAY_TH_CLASS,
  U3130_HONORARIUM_FOOTER_GUTTER_TD_CLASS,
  U3130_HONORARIUM_FOOTER_HOUR_TD_CLASS,
  U3130_HONORARIUM_FOOTER_LABEL_TD_CLASS,
  U3130_HONORARIUM_SUBPROJECT_COL_CLASS,
  u3130HonorariumTableMinWidth,
} from '../../tools/u3130HonorariumGridCompactStyles';
import U3130HonorariumProjectHeaderRow from './U3130HonorariumProjectHeaderRow';
import U3130HonorariumReadonlyTaskRow from './U3130HonorariumReadonlyTaskRow';
import U3130HonorariumSortableMetaHeader from './U3130HonorariumSortableMetaHeader';

export type U3130HonorariumFooterTotals = {
  daySums: Record<string, number>;
  grandTotal: number;
};

export interface U3130HonorariumReadonlyGridProps {
  rows: U3130HonorariumItem[];
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
  visibleDays: readonly U2000TimesheetWeekDay[];
  showSubProjectColumn: boolean;
  renderDayHeader: (day: U2000TimesheetWeekDay) => React.ReactNode;
  formatHours: (value: number) => string;
  projectTotals: U3130HonorariumFooterTotals;
  absenceTotals: U3130HonorariumFooterTotals;
  specialNetTotals: U3130HonorariumFooterTotals;
  workedTotals: U3130HonorariumFooterTotals;
}

/**
 * Grille honoraires en lecture (approbation, BL-0676) — même rendu que U3129 : un dossier par ligne client,
 * tâches visibles en dépliant (un seul dossier ouvert à la fois). Tri par défaut : numéro de dossier croissant.
 */
const U3130HonorariumReadonlyGrid: React.FC<U3130HonorariumReadonlyGridProps> = ({
  rows,
  formStateHandlers,
  visibleDays,
  showSubProjectColumn,
  renderDayHeader,
  formatHours,
  projectTotals,
  absenceTotals,
  specialNetTotals,
  workedTotals,
}) => {
  const { t } = useTranslation();
  const [expandedProjectKey, setExpandedProjectKey] = useState<string | null>(null);
  const [projectSortField, setProjectSortField] = useState<U3130HonorariumProjectSortField>('project');
  const [projectSortOrder, setProjectSortOrder] = useState<U3130HonorariumSortOrder>('ASC');

  const projectGroups = useMemo(
    () => u3130SortHonorariumProjectGroups(u3130GroupHonorariumForDisplay(rows), projectSortField, projectSortOrder),
    [rows, projectSortField, projectSortOrder],
  );

  const handleProjectSort = useCallback(
    (field: U3130HonorariumProjectSortField) => {
      if (field === projectSortField) {
        setProjectSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
        return;
      }
      setProjectSortField(field);
      setProjectSortOrder('ASC');
    },
    [projectSortField],
  );

  const toggleProjectExpanded = useCallback((projectKey: string) => {
    setExpandedProjectKey((prev) => (prev === projectKey ? null : projectKey));
  }, []);

  const metaColCount = showSubProjectColumn ? 4 : 3;
  const totalColCount = 1 + metaColCount + visibleDays.length + 1;
  const tableMinWidth = u3130HonorariumTableMinWidth({ showSubProjectColumn, dayColumnCount: visibleDays.length });

  const footerRow = (labelKey: string, labelDefault: string, totals: U3130HonorariumFooterTotals, emphasis: 'strong' | 'muted') => {
    const label = t(labelKey, { ns: NS, defaultValue: labelDefault });
    return (
      <tr aria-label={label}>
        <td className={U3130_HONORARIUM_FOOTER_GUTTER_TD_CLASS} />
        <td
          className={`${U3130_HONORARIUM_FOOTER_LABEL_TD_CLASS} ${emphasis === 'strong' ? 'font-semibold text-gray-800' : 'text-sm font-medium text-gray-600'}`}
          colSpan={metaColCount}
        >
          {label}
        </td>
        {visibleDays.map((d) => (
          <td key={d} className={`${U3130_HONORARIUM_FOOTER_HOUR_TD_CLASS} ${emphasis === 'strong' ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
            {(totals.daySums[d] ?? 0).toFixed(2)}
          </td>
        ))}
        <td className={`${U3130_HONORARIUM_FOOTER_HOUR_TD_CLASS} ${emphasis === 'strong' ? 'font-bold text-gray-900' : 'font-semibold text-gray-600'}`}>
          {totals.grandTotal.toFixed(2)}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-w-0 overflow-x-auto rounded-lg border border-gray-200">
      <table className="table-fixed border-collapse text-sm tabular-nums" style={{ width: '100%', minWidth: tableMinWidth }}>
        <colgroup>
          <col style={{ width: U3130_HONORARIUM_COL_WIDTH_CHECKBOX }} />
          <col style={{ width: U3130_HONORARIUM_COL_WIDTH_PROJECT, minWidth: U3130_HONORARIUM_COL_WIDTH_PROJECT }} />
          <col style={{ minWidth: U3130_HONORARIUM_COL_WIDTH_CLIENT_MIN }} />
          <col style={{ width: U3130_HONORARIUM_COL_WIDTH_NATURES, minWidth: U3130_HONORARIUM_COL_WIDTH_NATURES }} />
          {showSubProjectColumn ? (
            <col style={{ width: U3130_HONORARIUM_COL_WIDTH_SUBPROJECT, minWidth: U3130_HONORARIUM_COL_WIDTH_SUBPROJECT }} />
          ) : null}
          {visibleDays.map((d) => (
            <col key={d} style={{ width: U3130_HONORARIUM_COL_WIDTH_HOUR, minWidth: U3130_HONORARIUM_COL_WIDTH_HOUR }} />
          ))}
          <col style={{ width: U3130_HONORARIUM_COL_WIDTH_HOUR, minWidth: U3130_HONORARIUM_COL_WIDTH_HOUR }} />
        </colgroup>
        <thead className="border-b border-gray-200 bg-muted">
          <tr>
            <th className="w-10 pl-2 pr-1 py-2.5" />
            <U3130HonorariumSortableMetaHeader
              labelKey="lblProject"
              labelDefault="Projet"
              active={projectSortField === 'project'}
              order={projectSortOrder}
              onSort={() => handleProjectSort('project')}
            />
            <U3130HonorariumSortableMetaHeader
              labelKey="lblCustomer"
              labelDefault="Client"
              active={projectSortField === 'customer'}
              order={projectSortOrder}
              onSort={() => handleProjectSort('customer')}
            />
            <U3130HonorariumSortableMetaHeader
              labelKey="lblWorkNatures"
              labelDefault="Natures"
              active={projectSortField === 'natures'}
              order={projectSortOrder}
              onSort={() => handleProjectSort('natures')}
            />
            {showSubProjectColumn ? (
              <th className={`${U3130_HONORARIUM_CELL_X} py-2.5 text-left font-medium text-gray-700 ${U3130_HONORARIUM_SUBPROJECT_COL_CLASS}`}>
                {t('lblSubProject', { ns: NS, defaultValue: 'Sous-projet' })}
              </th>
            ) : null}
            {visibleDays.map((d) => (
              <th key={d} className={U3130_HONORARIUM_DAY_TH_CLASS}>
                {renderDayHeader(d)}
              </th>
            ))}
            <th className={U3130_HONORARIUM_DAY_TH_CLASS}>Total</th>
          </tr>
        </thead>
        <tbody>
          {projectGroups.length === 0 ? (
            <tr>
              <td colSpan={totalColCount} className="px-4 py-8 text-center text-sm text-gray-400">
                {t('msgNoHonorariumProjectLines', { ns: NS, defaultValue: 'Aucun projet pour cette période.' })}
              </td>
            </tr>
          ) : (
            projectGroups.map((group, groupIndex) => {
              const expanded = expandedProjectKey === group.projectKey;
              return (
                <React.Fragment key={group.projectKey}>
                  <U3130HonorariumProjectHeaderRow
                    group={group}
                    expanded={expanded}
                    onToggle={() => toggleProjectExpanded(group.projectKey)}
                    visibleDays={visibleDays}
                    showSubProjectColumn={showSubProjectColumn}
                    isFirstProject={groupIndex === 0}
                    formatHours={formatHours}
                  />
                  {expanded
                    ? group.lines.map(({ index, row }) => (
                        <U3130HonorariumReadonlyTaskRow
                          key={row.clientRowId?.trim() || row.id || `idx-${index}`}
                          index={index}
                          row={row}
                          formStateHandlers={formStateHandlers}
                          visibleDays={visibleDays}
                          showSubProjectColumn={showSubProjectColumn}
                          formatHours={formatHours}
                        />
                      ))
                    : null}
                </React.Fragment>
              );
            })
          )}
        </tbody>
        <tfoot className="border-t-2 border-gray-200 bg-muted shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.05)]">
          {footerRow('lblTotalProjectHours', 'Total projet', projectTotals, 'strong')}
          {footerRow('lblCrossTotalAbsenceHours', 'Total absence', absenceTotals, 'muted')}
          {footerRow('lblCrossTotalSpecialBankNetHours', 'Total heures accumulées (net)', specialNetTotals, 'muted')}
          {footerRow('lblHeaderTotalHonorariumHours', 'Total honoraires', workedTotals, 'strong')}
        </tfoot>
      </table>
    </div>
  );
};

export default U3130HonorariumReadonlyGrid;
